import { useHideAnimation } from "@-/com.npmjs.react-native-bootsplash";
import { Image } from "@-/com.npmjs.expo-image";
import { WelcomeHaniContext } from "#use-welcome-hani-greeting";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  AppState,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
} from "@-/com.npmjs.react-native";
import Animated, {
  cancelAnimation,
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withSequence,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import {
  isBootsplashRevealed,
  whenBootsplashRevealed,
} from "@-/dev.improve.haniverse.lib.bootsplash";
import BOOTSPLASH_HANI from "@-/dev.improve.haniverse.assets.logo/@images/bootsplash-logo.png";
import { useLearnMapModeSelector } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { createWelcomeHaniGreeting } from "#create-welcome-hani-greeting";
import {
  WELCOME_HANI_SPLASH_SIZE,
  welcomeHaniSize,
  welcomeHaniTop,
  resetWelcomeHaniMotion,
  welcomeHaniEntrance,
} from "#welcome-hani-motion";

/**
 * One owner above map suspense and sheet remounts. Each real Welcome Back entry gets a greeting;
 * returning from the background starts another greeting; ordinary rerenders do not.
 * The splash is a prerendered 3D PNG, not a live model. Keep that exact artwork throughout this
 * handoff; the separate 2D onboarding mascot never replaces it.
 */
export function WelcomeHaniProvider({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  const { height, width } = useWindowDimensions();
  const reduceMotion = useReducedMotion();
  // Use the library's native logo ratio (including Samsung One UI 4). Never give this hook
  // hide ownership: the existing boot coordinator controls reveal and its bounded fallback.
  const { logo: nativeLogo } = useHideAnimation({
    animate: () => {},
    logo: BOOTSPLASH_HANI,
    manifest: {
      background: "#ffffff",
      logo: { height: WELCOME_HANI_SPLASH_SIZE, width: WELCOME_HANI_SPLASH_SIZE },
    },
    ready: false,
  });
  const nativeLogoWidth = StyleSheet.flatten(nativeLogo.style)?.width;
  const splashSize =
    typeof nativeLogoWidth === "number" ? nativeLogoWidth : WELCOME_HANI_SPLASH_SIZE;
  const absentSheet = useSharedValue(Number.POSITIVE_INFINITY);
  const sheetTop =
    useLearnMapModeSelector((mode) => mode.perchSheetTop ?? mode.sheetTop) ?? absentSheet;
  const [sheetReady, setSheetReady] = useState(false);
  const [registrations, setRegistrations] = useState(0);
  const [imageReady, setImageReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [originX, setOriginX] = useState(0);
  const [foreground, setForeground] = useState(() => AppState.currentState !== "background");
  const [foregroundEntry, setForegroundEntry] = useState(0);
  const [originY, setOriginY] = useState(0);
  const [visible, setVisible] = useState(false);
  const [bootHidden, setBootHidden] = useState(
    () => Platform.OS === "web" || isBootsplashRevealed(),
  );
  const splashArmed = useRef(false);
  const started = useRef(false);
  const arrived = useRef(false);
  const pressed = useRef(false);
  const visit = useRef(0);
  const root = useRef<View>(null);
  const progress = useSharedValue(1);
  const delight = useSharedValue(0);
  const greeting = useRef<ReturnType<typeof createWelcomeHaniGreeting> | null>(null);
  const eligible = active && registrations > 0 && foreground;

  useEffect(() => {
    let backgrounded = AppState.currentState === "background";
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "background") {
        backgrounded = true;
        greeting.current?.cancel();
        cancelAnimation(progress);
        cancelAnimation(delight);
        setForeground(false);
      } else if (state === "active" && backgrounded) {
        backgrounded = false;
        setForeground(true);
        setForegroundEntry((entry) => entry + 1);
      }
    });
    return () => subscription.remove();
  }, [delight, progress]);

  useAnimatedReaction(
    () => Number.isFinite(sheetTop.get()) && sheetTop.get() > 0,
    (ready, previous) => {
      if (ready !== previous) {
        runOnJS(setSheetReady)(ready);
      }
    },
  );
  useEffect(() => {
    if (Platform.OS === "web") {
      return;
    }
    let mounted = true;
    void whenBootsplashRevealed().then(() => {
      if (mounted) {
        setBootHidden(true);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);
  const markArrived = useCallback((completedVisit: number) => {
    if (visit.current === completedVisit && !pressed.current) {
      arrived.current = true;
    }
  }, []);
  const motion = useRef({ delight, progress });
  motion.current = { delight, progress };
  useEffect(() => {
    const { delight, progress } = motion.current;
    visit.current++;
    resetWelcomeHaniMotion(progress, delight);
    started.current = false;
    splashArmed.current = false;
    arrived.current = false;
    pressed.current = false;
    setVisible(false);
    if (!eligible) {
      return;
    }
    const current = createWelcomeHaniGreeting({
      onContinue: () => {},
      onPhase: (phase) => {
        switch (phase) {
          case "happy": {
            // The original smiling 3D portrait celebrates with a short buoyant lift and tilt.
            delight.set(
              withSequence(withTiming(1, { duration: 70 }), withTiming(0, { duration: 100 })),
            );

            break;
          }
          case "ducking": {
            cancelAnimation(progress);
            progress.set(withTiming(3, { duration: 260 }));

            break;
          }
          case "hidden": {
            cancelAnimation(progress);
            setVisible(false);

            break;
          }
          // No default
        }
      },
    });
    greeting.current = current;
    return () => {
      current.cancel();
      if (greeting.current === current) {
        greeting.current = null;
      }
      cancelAnimation(progress);
      cancelAnimation(delight);
    };
  }, [eligible, foregroundEntry]);

  useEffect(() => {
    if (started.current || pressed.current) {
      return;
    }
    const entrance = welcomeHaniEntrance({
      armed: splashArmed.current,
      ready: eligible && imageReady && !failed && sheetReady,
      revealed: bootHidden || isBootsplashRevealed(),
    });
    if (entrance === "wait") {
      return;
    }
    if (entrance === "arm") {
      splashArmed.current = true;
      // Match the native splash exactly while its background dissolves. Hani itself stays opaque.
      progress.set(0);
      setVisible(true);
      return;
    }
    const fromSplash = entrance === "descend";
    started.current = true;
    splashArmed.current = false;
    setVisible(true);
    if (reduceMotion) {
      progress.set(2);
      markArrived(visit.current);
      return;
    }
    const enteringVisit = visit.current;
    const rise = withTiming(2, { duration: 300 }, (finished) => {
      if (finished) {
        runOnJS(markArrived)(enteringVisit);
      }
    });
    progress.set(fromSplash ? 0 : 1);
    progress.set(fromSplash ? withSequence(withTiming(1, { duration: 280 }), rise) : rise);
  }, [
    bootHidden,
    eligible,
    failed,
    foregroundEntry,
    imageReady,
    markArrived,
    progress,
    reduceMotion,
    sheetReady,
  ]);

  useEffect(() => {
    if (reduceMotion && bootHidden && eligible && started.current) {
      cancelAnimation(progress);
      cancelAnimation(delight);
      progress.set(pressed.current ? 3 : 2);
      delight.set(0);
    }
  }, [bootHidden, delight, eligible, progress, reduceMotion]);

  const currentOptions = useRef({ imageReady, reduceMotion });
  currentOptions.current = { imageReady, reduceMotion };
  const [context] = useState(() => ({
    continueLearning: () => {
      if (pressed.current) {
        return false;
      }
      pressed.current = true;
      return (
        greeting.current?.continueLearning({
          artworkReady: currentOptions.current.imageReady && arrived.current,
          reduceMotion: currentOptions.current.reduceMotion,
        }) ?? true
      );
    },
    register: () => {
      setRegistrations((count) => count + 1);
      return () => setRegistrations((count) => count - 1);
    },
  }));
  const clipStyle = useAnimatedStyle(() => ({
    height:
      progress.get() < 1
        ? height
        : Number.isFinite(sheetTop.get())
          ? Math.max(0, sheetTop.get() - originY)
          : 0,
    opacity: visible && eligible && sheetReady && !failed ? 1 : 0,
  }));
  const portraitStyle = useAnimatedStyle(() => ({
    height: welcomeHaniSize(progress.get(), splashSize),
    left: (width - welcomeHaniSize(progress.get(), splashSize)) / 2 - originX,
    top:
      welcomeHaniTop(
        progress.get(),
        Number.isFinite(sheetTop.get()) ? sheetTop.get() : height,
        height,
        splashSize,
      ) - originY,
    transform: [
      { translateY: -6 * delight.get() },
      { rotate: `${-5 * delight.get()}deg` },
      { scale: 1 + 0.06 * delight.get() },
    ],
    width: welcomeHaniSize(progress.get(), splashSize),
  }));
  return (
    <WelcomeHaniContext value={context}>
      <View
        className="absolute inset-0"
        onLayout={() =>
          root.current?.measureInWindow((x, y) => {
            setOriginX(x);
            setOriginY(y);
          })
        }
        pointerEvents="box-none"
        ref={root}
      >
        {children}
        <Animated.View
          accessibilityElementsHidden
          className="absolute inset-x-0 top-0 overflow-hidden"
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          style={clipStyle}
          testID="welcome-back-hani"
        >
          <Animated.View className="absolute" style={portraitStyle}>
            <Image
              contentFit="contain"
              onError={() => {
                setFailed(true);
                setVisible(false);
              }}
              onLoad={() => {
                setImageReady(true);
              }}
              source={BOOTSPLASH_HANI}
              style={styles.image}
              transition={0}
            />
          </Animated.View>
        </Animated.View>
      </View>
    </WelcomeHaniContext>
  );
}

const styles = StyleSheet.create({ image: { height: "100%", width: "100%" } });
